import React, { useState, useEffect, useRef } from "react";
import { Form, Select, DatePicker, Button } from "antd";
import { Chart, registerables } from "chart.js/auto";
import { useDispatch, useSelector } from "react-redux";
import { getStates } from "../features/statecity/statecitySlice";
import { getAssesmentList } from "../features/assesments/assesmentSlice";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

Chart.register(...registerables);

const AssessmentStatus = () => {
  const chartInstanceRef = useRef(null);
  const chartInstance = useRef(null);
  const dispatch = useDispatch();
  const [selectedState, setSelectedState] = useState("");
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [filteredData, setFilteredData] = useState({});
  const [chartData, setChartData] = useState(null);
  const [summaryData, setSummaryData] = useState({});
  const [form] = Form.useForm();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pdfExportRef = useRef(null);

  useEffect(() => {
    dispatch(getStates());
    dispatch(getAssesmentList());
  }, [dispatch]);

  const statelistState = useSelector(
    (state) => state.statecity.statelist || []
  );
  const assessmentState = useSelector(
    (state) => state.assesmentlist?.assessmentlists || []
  );

  // Process data for chart and table
  const processData = (data, filters = {}) => {
    if (!data || data.length === 0) {
      return { labels: [], datasets: [], tableData: {}, summaryData: {} };
    }

    const { state, start_date, end_date } = filters;

    const filtered = data.filter((item) => {
      const stateMatch = !state || item.stateName === state;
      const dateMatch =
        !start_date ||
        !end_date ||
        (new Date(item.createdAt) >= new Date(start_date) &&
          new Date(item.createdAt) <= new Date(end_date));
      return stateMatch && dateMatch;
    });

    const stateWiseData = filtered.reduce((acc, data) => {
      const stateName = data.stateName || "Unknown";
      acc[stateName] = acc[stateName] || {
        total: 0,
        present: 0,
        absent: 0,
        pass: 0,
        fail: 0,
      };
      acc[stateName].total += data.totalAssessments || 0;
      acc[stateName].present +=
        (data.totalAssessments || 0) - (data.absent || 0);
      acc[stateName].absent += data.absent || 0;
      acc[stateName].pass += data.pass || 0;
      acc[stateName].fail += data.fail || 0;
      return acc;
    }, {});

    const labels = Object.keys(stateWiseData);
    const datasets = [
      {
        label: "Present",
        data: labels.map((state) => stateWiseData[state].present),
        backgroundColor: "#4CAF50",
      },
      {
        label: "Absent",
        data: labels.map((state) => stateWiseData[state].absent),
        backgroundColor: "#FFC107",
      },
      {
        label: "Passed",
        data: labels.map((state) => stateWiseData[state].pass),
        backgroundColor: "#2196F3",
      },
      {
        label: "Failed",
        data: labels.map((state) => stateWiseData[state].fail),
        backgroundColor: "#F44336",
      },
    ];

    const summaryData = {
      total: Object.values(stateWiseData).reduce(
        (sum, state) => sum + state.total,
        0
      ),
      pass: Object.values(stateWiseData).reduce(
        (sum, state) => sum + state.pass,
        0
      ),
      passPercentage: (
        (Object.values(stateWiseData).reduce(
          (sum, state) => sum + state.pass,
          0
        ) /
          Object.values(stateWiseData).reduce(
            (sum, state) => sum + state.total,
            0
          )) *
        100
      ).toFixed(2),
    };

    return { labels, datasets, tableData: stateWiseData, summaryData };
  };

  // Set initial data
  useEffect(() => {
    if (assessmentState.length) {
      const { labels, datasets, tableData, summaryData } =
        processData(assessmentState);
      setFilteredData(tableData);
      setChartData({ labels, datasets });
      setSummaryData(summaryData);
    }
  }, [assessmentState]);

  const handleSubmit = (values) => {
    const filters = {
      state: values.state,
      start_date: values.start_date
        ? values.start_date.format("YYYY-MM-DD")
        : null,
      end_date: values.end_date ? values.end_date.format("YYYY-MM-DD") : null,
    };

    const { labels, datasets, tableData, summaryData } = processData(
      assessmentState,
      filters
    );
    setFilteredData(tableData);
    setChartData({ labels, datasets });
    setSummaryData(summaryData);
  };

  const resetFilters = () => {
    form.resetFields();
    setSelectedState("");
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    const { labels, datasets, tableData, summaryData } =
      processData(assessmentState);
    setFilteredData(tableData);
    setChartData({ labels, datasets });
    setSummaryData(summaryData);
  };

  // Chart rendering
  useEffect(() => {
    if (chartInstanceRef.current && chartData) {
      const ctx = chartInstanceRef.current.getContext("2d");
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
      chartInstance.current = new Chart(ctx, {
        type: "bar",
        data: chartData,
        options: {
          responsive: true,
          plugins: { legend: { position: "top" } },
          scales: {
            y: { beginAtZero: true, title: { display: true, text: "Count" } },
            x: { title: { display: true, text: "States" } },
          },
        },
      });
    }
  }, [chartData]);

  // Pagination logic
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (value) => {
    setPageSize(value);
    setCurrentPage(1); // Reset to first page when size changes
  };

  const tableArray = Object.entries(filteredData).map(([state, data]) => ({
    state,
    ...data,
  }));
  const paginatedData = tableArray.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );
  const totalPages = Math.ceil(tableArray.length / pageSize);

  const exportAsPDF = () => {
    const input = pdfExportRef.current;
    html2canvas(input).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save("assessment_status_report.pdf");
    });
  };

  const styles = {
    table: {
      width: "100%",
      borderCollapse: "collapse",
    },
    th: {
      padding: "10px",
      textAlign: "left",
      backgroundColor: "#ddd",
    },
    td: {
      padding: "10px",
    },
    button: {
      padding: "10px 10px",
      color: "#fff",
      border: "none",
      cursor: "pointer",
      marginTop: "20px",
    },
  };

  return (
    <div
      style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}
      ref={pdfExportRef}
    >
      <h4
        style={{
          fontSize: "large",
          fontWeight: "bold",
          margin: "-20px 0 20px -550px",
        }}
      >
        Assessment Overview
      </h4>
      <Form
        form={form}
        layout="inline"
        onFinish={handleSubmit}
        style={{ marginTop: "30px", marginLeft: "30px", gap: "10px" }}
      >
        <Form.Item
          name="state"
          rules={[{ required: true, message: "Please select a state!" }]}
        >
          <Select
            placeholder="Select State"
            onChange={(value) => setSelectedState(value)}
            value={selectedState}
            style={{ width: 150 }}
          >
            {statelistState.map((state) => (
              <Select.Option key={state.code} value={state.code}>
                {state.state_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="start_date"
          rules={[{ required: true, message: "Select date!" }]}
        >
          <DatePicker
            placeholder="From"
            style={{ width: 100 }}
            onChange={(value) => {
              setSelectedStartDate(value ? value.format("YYYY-MM-DD") : null);
            }}
          />
        </Form.Item>
        <Form.Item
          name="end_date"
          rules={[{ required: true, message: "Select date!" }]}
        >
          <DatePicker
            placeholder="To"
            style={{ width: 100 }}
            onChange={(value) => {
              setSelectedEndDate(value ? value.format("YYYY-MM-DD") : null);
            }}
          />
        </Form.Item>
        <Form.Item>
          <Button
            style={{ backgroundColor: "#28a745", color: "#fff" }}
            htmlType="submit"
          >
            Apply Filter
          </Button>
        </Form.Item>
        <Form.Item>
          <Button
            style={{ backgroundColor: "#dc3545", color: "#fff" }}
            onClick={resetFilters}
          >
            Reset Filter
          </Button>
        </Form.Item>
      </Form>

      <div
        style={{
          backgroundColor: "#DCDCDC",
          background: "#fff",
          borderRadius: "8px",
          boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
          // padding: "25px",
          // marginTop: "25px",
        }}
      >
        <canvas ref={chartInstanceRef} height="100%" width="100%"></canvas>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "16px",
          marginTop: "25px",
        }}
      >
        <div
          style={{
            backgroundColor: "#bfdbfe",
            padding: "15px",
            height: "90px",
            borderRadius: "8px",
            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <p style={{ fontWeight: "bold" }}>Total Students Assessed</p>
          <p style={{ marginTop: "-10px" }}>{summaryData.total || 0}</p>
        </div>
        <div
          style={{
            backgroundColor: "#bbf7d0",
            padding: "15px",
            height: "90px",
            borderRadius: "8px",
            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <p style={{ fontWeight: "bold" }}>Total Passed</p>
          <p style={{ marginTop: "-10px" }}>{summaryData.pass || 0}</p>
        </div>
        <div
          style={{
            backgroundColor: "#fef9c3",
            padding: "15px",
            height: "90px",
            borderRadius: "8px",
            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <p style={{ fontWeight: "bold" }}>Overall Pass Percentage</p>
          <p style={{ marginTop: "-10px" }}>
            {summaryData.passPercentage || 0}%
          </p>
        </div>
      </div>

      <div
        className="table-container"
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "0.5rem",
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
          padding: "1rem",
          marginTop: "1.5rem",
        }}
      >
        <div
          style={{
            marginBottom: "10px",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <div>
            Showing {paginatedData.length} of {tableArray.length} entries
          </div>
          <div>
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              style={{ width: 100 }}
            >
              <Select.Option value={10}>10</Select.Option>
              <Select.Option value={20}>20</Select.Option>
              <Select.Option value={50}>50</Select.Option>
              <Select.Option value={100}>100</Select.Option>
            </Select>
          </div>
        </div>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>State</th>
              <th style={styles.th}>No. Student</th>
              <th style={styles.th}>No. Present</th>
              <th style={styles.th}>No. Absent</th>
              <th style={styles.th}>No. Passed</th>
              <th style={styles.th}>No. Failed</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((data, index) => (
              <tr key={index}>
                <td style={styles.td}>{data.state}</td>
                <td style={styles.td}>{data.total}</td>
                <td style={styles.td}>{data.present}</td>
                <td style={styles.td}>{data.absent}</td>
                <td style={styles.td}>{data.pass}</td>
                <td style={styles.td}>{data.fail}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: "10px", textAlign: "center" }}>
          <Button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            style={{ marginRight: "10px" }}
          >
            Previous
          </Button>
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <Button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{ marginLeft: "10px" }}
          >
            Next
          </Button>
        </div>
      </div>

      <div style={{ margin: "2px 0 0 20px", display: "flex", gap: "30px" }}>
        <button
          style={{
            ...styles.button,
            backgroundColor: "#28a745",
            borderRadius: "8px",
          }}
          onClick={exportAsPDF}
        >
          Export as PDF
        </button>
        <button
          style={{
            ...styles.button,
            backgroundColor: "#28a745",
            borderRadius: "8px",
            marginRight: "100px",
          }}
        >
          Export as CSV
        </button>
      </div>
    </div>
  );
};

export default AssessmentStatus;
